/**
 * The five predefined themes (web.md §7) as ThemeDefinition constants (contracts/settings.ts).
 *
 *   dark      the prototype's default — its colours are exactly what ui.css + refinements.css compute
 *   light     the prototype's light theme (ui.css `:root[data-theme=light]`)
 *   paper     warm light, serif reading
 *   midnight  true-black dark
 *   mono      monochrome, the brand direction (black and white, green/red signals only)
 *
 * `dark` and `light` are rendered by the stylesheet itself (so the accent choices of themes.css keep
 * working); the other three are written as CSS custom properties by apply.ts. Free gets the first two.
 */

import { DEFAULT_CUSTOM_THEME, PREDEFINED_THEME_IDS, type PredefinedThemeId, type ThemeColorKey, type ThemeDefinition } from '@digester/core';

type Colors = Record<ThemeColorKey, `#${string}`>;

const base: Omit<ThemeDefinition, 'name' | 'base' | 'colors'> = {
  uiFont: 'Inter',
  readingFont: 'Inter',
  uiFamily: '',
  readingFamily: '',
  shadows: true,
  uiSize: 15,
  readingSize: 18,
  headingSize: 28,
  uiLineHeight: 1.5,
  readingLineHeight: 1.8,
  letterSpacing: 0,
  rowPadding: 16,
  readerPadding: 34,
  sectionGap: 26,
  controlHeight: 42,
  toolbarHeight: 72,
  radius: 8,
  borderWidth: 1,
  iconSize: 20,
  navWidth: DEFAULT_CUSTOM_THEME.navWidth,
  listWidth: DEFAULT_CUSTOM_THEME.listWidth,
  detailsWidth: DEFAULT_CUSTOM_THEME.detailsWidth,
  articleWidth: 700,
};

/** Effective prototype dark: ui.css `:root` with the refinements.css overrides (muted, dim, body, border-light). */
const DARK: Colors = {
  page: '#121216', nav: '#18181e', list: '#1b1b22', reader: '#202027', details: '#19191f', surface: '#272730',
  'surface-hover': '#30303b', border: '#303039', 'border-light': '#41414d', text: '#eae8ef', body: '#d7d4df', muted: '#bbb5c6',
  dim: '#aaa3b4', accent: '#c4b0fb', 'accent-fill': '#645194', 'accent-bg': '#383047', selected: '#2e293b', green: '#9cc4ac',
};

const LIGHT: Colors = {
  page: '#f6f6f9', nav: '#f6f5f8', list: '#ffffff', reader: '#ffffff', details: '#faf9fc', surface: '#f2f0f6',
  'surface-hover': '#e9e6f0', border: '#e6e3ed', 'border-light': '#d6d1e1', text: '#292532', body: '#423c4e', muted: '#6f657c',
  dim: '#72677c', accent: '#6d50ac', 'accent-fill': '#7556b1', 'accent-bg': '#e9e1f5', selected: '#f1ecf8', green: '#427656',
};

const PAPER: Colors = {
  page: '#f4efe6', nav: '#efe8dc', list: '#fbf8f2', reader: '#fdfaf4', details: '#f6f1e8', surface: '#ece4d6',
  'surface-hover': '#e3d9c8', border: '#e2d8c7', 'border-light': '#d3c6b0', text: '#2b241b', body: '#3d3428', muted: '#6c5f4c',
  dim: '#76695a', accent: '#8a4b22', 'accent-fill': '#9a5629', 'accent-bg': '#f1e0cf', selected: '#f3e6d6', green: '#4c7350',
};

const MIDNIGHT: Colors = {
  page: '#000000', nav: '#08080a', list: '#0b0b0e', reader: '#0e0e12', details: '#09090c', surface: '#18181d',
  'surface-hover': '#222229', border: '#1f1f26', 'border-light': '#33333d', text: '#eeecf3', body: '#d5d2dc', muted: '#b4aec0',
  dim: '#a39dac', accent: '#c6b4ff', 'accent-fill': '#5b46a0', 'accent-bg': '#241d38', selected: '#1d1830', green: '#98c7a8',
};

const MONO: Colors = {
  page: '#0d0d0d', nav: '#121212', list: '#151515', reader: '#191919', details: '#131313', surface: '#222222',
  'surface-hover': '#2c2c2c', border: '#2a2a2a', 'border-light': '#3d3d3d', text: '#f2f2f2', body: '#d9d9d9', muted: '#b8b8b8',
  dim: '#a6a6a6', accent: '#ffffff', 'accent-fill': '#4d4d4d', 'accent-bg': '#333333', selected: '#262626', green: '#10b981',
};

export const THEME_PRESETS: Readonly<Record<PredefinedThemeId, ThemeDefinition>> = {
  dark: { ...base, name: 'Dark', base: 'dark', colors: DARK },
  light: { ...base, name: 'Light', base: 'light', colors: LIGHT },
  paper: { ...base, name: 'Paper', base: 'light', readingFont: 'Georgia', readingLineHeight: 1.75, colors: PAPER },
  midnight: { ...base, name: 'Midnight', base: 'dark', shadows: false, colors: MIDNIGHT },
  mono: { ...base, name: 'Mono', base: 'dark', colors: MONO },
};

/** Themes the stylesheet renders by `data-theme` alone (no inline colour properties). */
export const STYLESHEET_THEMES: ReadonlySet<PredefinedThemeId> = new Set(['dark', 'light']);

export const PRESET_ORDER: readonly PredefinedThemeId[] = PREDEFINED_THEME_IDS;
